import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Alert from '@material-ui/lab/Alert'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import { Link as RouterLink } from 'react-router-dom'
import { MY_PGA_COM_HOST } from 'env'
import TipWell from 'components/tip-well'
import CreditCardIcon from 'components/material-symbol/icons/CreditCard'
import LockIcon from 'components/material-symbol/icons/Lock'
import { redirectToLogin, redirectToSignup } from 'lib/auth'
import { centsToDollarsFormatted } from 'lib/utils/number'
import StripePaymentElementsForm from 'pages/schedule/BuyLessonPacksLayout/stripe-payment-elements-form'
import MarketingOptInCard from './marketing-opt-in-card'
import { useStyles } from './styles'
import SwingAnalysisAccountModal from './swing-analysis-account-modal'
import { getMessageCoachPath } from './utils'

// Where Stripe sends the golfer after paying. `lessonTypeId` lets the
// confirmation page offer "Try again" if the payment did not go through.
export const getReturnUrl = (idOrSlug, lessonTypeId) =>
  `${MY_PGA_COM_HOST}/coach/${idOrSlug}/purchase/virtual-coaching?lessonTypeId=${encodeURIComponent(
    lessonTypeId,
  )}`

const SignInGate = () => {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Box className={classes.signInTip}>
      <TipWell
        variant="neutral"
        stacked
        caption={
          <>
            <Box component="span" className={classes.tipTitle}>
              Sign in or create an account
            </Box>
            <Box component="span" className={classes.tipBody}>
              You need a MyPGA account to buy Virtual Coaching so we can send
              your videos to your coach and share their feedback with you.{' '}
              <button
                type="button"
                className={classes.learnMore}
                onClick={() => setIsOpen(true)}
              >
                Learn more
              </button>
            </Box>
          </>
        }
        action={
          <Box className={classes.tipActions}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => redirectToSignup('consumer')}
            >
              Create Account
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => redirectToLogin('consumer')}
            >
              Sign In
            </Button>
          </Box>
        }
      />
      <SwingAnalysisAccountModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </Box>
  )
}

const PurchaseForm = ({
  offering,
  idOrSlug,
  lessonTypeId,
  user,
  prepare,
  PaymentForm,
}) => {
  const classes = useStyles()
  const stripePaymentFormRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(null)
  const [payError, setPayError] = useState(null)
  const { clientSecret, loading, errorMessage, retry } = prepare
  const userIsNotOptedIn = !user?.marketingOptInAt
  const price = centsToDollarsFormatted(offering.priceInCents) || '$0.00'
  const messageCoachPath = getMessageCoachPath(offering.coach)

  // Pay stays disabled only until the payment intent exists or while a payment
  // is processing. Say so under the button, and keep the last Stripe message
  // (incomplete card, declined) visible: the toast alone is easy to miss.
  let payHint = payError
  if (!payHint && !clientSecret) {
    payHint = 'Secure checkout is getting ready. Pay turns on in a moment.'
  }

  const handlePay = () => {
    setPayError(null)
    stripePaymentFormRef.current?.submitPayment?.()
  }

  if (errorMessage) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={retry}>
            Try again
          </Button>
        }
      >
        {errorMessage}
      </Alert>
    )
  }

  return (
    <Box>
      <Box aria-live="polite" aria-busy={loading}>
        {loading ? (
          <>
            <LinearProgress />
            <Typography className={classes.helpLine}>
              Preparing secure checkout…
            </Typography>
          </>
        ) : null}
      </Box>
      <PaymentForm
        ref={stripePaymentFormRef}
        clientSecret={clientSecret}
        secretLoading={false}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        coachSlug={idOrSlug}
        returnUrl={getReturnUrl(idOrSlug, lessonTypeId)}
        showHeading={false}
        marketingOptInChecked={marketingOptInChecked}
        onSubmitError={setPayError}
      />
      {userIsNotOptedIn ? (
        <Box mt={3}>
          <MarketingOptInCard
            checked={marketingOptInChecked}
            onChange={setMarketingOptInChecked}
          />
        </Box>
      ) : null}
      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disableElevation
          onClick={handlePay}
          disabled={!clientSecret || isSubmitting}
          data-testid="pay-button"
          aria-describedby={payHint ? 'virtual-coaching-pay-hint' : undefined}
        >
          {isSubmitting ? 'Processing…' : `Pay ${price}`}
        </Button>
        {payHint && !isSubmitting ? (
          <Typography
            id="virtual-coaching-pay-hint"
            role={payError ? 'alert' : 'status'}
            data-testid="pay-hint"
            className={`${classes.payHint} ${
              payError ? classes.payHintError : ''
            }`}
          >
            {payHint}
          </Typography>
        ) : null}
      </Box>
      <Box className={classes.secureNote}>
        <LockIcon aria-hidden="true" />
        <span>Your payment is secured with SSL encryption</span>
      </Box>
      <Typography className={classes.helpLine}>
        Questions?{' '}
        {messageCoachPath ? (
          <Link component={RouterLink} to={messageCoachPath}>
            Message your coach
          </Link>
        ) : (
          'Message your coach'
        )}{' '}
        before you buy.
      </Typography>
    </Box>
  )
}

const PaymentPanel = ({ user, ...formProps }) => {
  const classes = useStyles()

  return (
    <section className={classes.card} aria-labelledby="payment-details-title">
      <Box className={classes.cardHeader}>
        <Box className={classes.cardTitleBlock}>
          <Box className={classes.cardTitleRow}>
            <CreditCardIcon aria-hidden="true" />
            <Typography
              id="payment-details-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              Payment details
            </Typography>
          </Box>
          <Typography className={classes.cardSubtitle}>
            Your card details are handled securely by Stripe and never stored
            here
          </Typography>
        </Box>
      </Box>
      <Box className={classes.cardBody}>
        {user ? <PurchaseForm user={user} {...formProps} /> : <SignInGate />}
      </Box>
    </section>
  )
}

PaymentPanel.propTypes = {
  user: PropTypes.object,
  offering: PropTypes.object.isRequired,
  idOrSlug: PropTypes.string.isRequired,
  lessonTypeId: PropTypes.string.isRequired,
  prepare: PropTypes.shape({
    clientSecret: PropTypes.string,
    loading: PropTypes.bool,
    errorMessage: PropTypes.string,
    retry: PropTypes.func,
  }).isRequired,
  PaymentForm: PropTypes.elementType,
}

PaymentPanel.defaultProps = {
  PaymentForm: StripePaymentElementsForm,
}

export default PaymentPanel
